import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from './firebase';
import { TeamRole } from './permissions';
import { logAuditEvent, AuditAction } from './audit';
import { User } from 'firebase/auth';
import { canCreateTeam } from './subscriptions';
import { logTeamActivity } from './team-activity';
import { createTeamReportConfig } from './team-reports';

export interface Team {
  id?: string;
  name: string;
  description?: string;
  memberIds: string[]; // Legacy - will be migrated to membership subcollection
  createdBy: string;
  createdAt: Timestamp | ReturnType<typeof serverTimestamp>;
  updatedAt?: Timestamp | ReturnType<typeof serverTimestamp>;
  membershipType?: 'open' | 'closed' | 'private'; // Default to closed for existing teams
  archived?: boolean;
  gradient?: {
    from: string;
    to: string;
  };
  icon?: string; // Lucide icon name
  deletedAt?: Timestamp | null; // Soft delete support
  isExample?: boolean; // Example content flag
  insightsVisibility?: 'managers' | 'team' | 'org'; // Who can see standup insights (default: managers)
  // GitHub repository configuration for changelog suggestions
  githubRepo?: {
    owner: string;
    name: string;
  };
}

export interface TeamMembership {
  id?: string;
  userId: string;
  role: TeamRole;
  addedAt: Timestamp | ReturnType<typeof serverTimestamp>;
  addedBy: string;
  updatedAt?: Timestamp | ReturnType<typeof serverTimestamp>;
}

const DEFAULT_GRADIENTS = [
  { from: '#6366f1', to: '#ec4899' }, // indigo → pink
  { from: '#0ea5e9', to: '#6366f1' }, // sky → indigo
  { from: '#10b981', to: '#0ea5e9' }, // emerald → sky
  { from: '#f59e0b', to: '#ef4444' }, // amber → red
  { from: '#8b5cf6', to: '#06b6d4' }, // violet → cyan
  { from: '#ec4899', to: '#f59e0b' }, // pink → amber
  { from: '#14b8a6', to: '#8b5cf6' }, // teal → violet
  { from: '#f97316', to: '#ec4899' }, // orange → pink
  { from: '#3b82f6', to: '#10b981' }, // blue → emerald
  { from: '#e11d48', to: '#7c3aed' }, // rose → purple
];

export function getTeamGradient(team: Team): string {
  if (team.gradient) {
    return `linear-gradient(to bottom right, ${team.gradient.from}, ${team.gradient.to})`;
  }
  let hash = 0;
  const id = team.id || team.name || '';
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  const g = DEFAULT_GRADIENTS[Math.abs(hash) % DEFAULT_GRADIENTS.length];
  return `linear-gradient(to bottom right, ${g.from}, ${g.to})`;
}

export async function createTeam(
  orgId: string,
  team: Omit<Team, 'id' | 'createdAt' | 'updatedAt'>,
  creator?: User
) {
  // Check subscription limits
  const limitCheck = await canCreateTeam(orgId);
  if (!limitCheck.allowed) {
    throw new Error(limitCheck.reason || 'Cannot create team: limit reached');
  }

  const batch = writeBatch(db);
  const teamRef = doc(collection(db, 'orgs', orgId, 'teams'));

  // Create team
  batch.set(teamRef, {
    ...team,
    membershipType: team.membershipType || 'closed', // Default to closed for backward compatibility
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    archived: false,
    deletedAt: null
  });

  // Add creator as team manager
  if (creator) {
    const memberRef = doc(collection(db, 'orgs', orgId, 'teams', teamRef.id, 'memberships'), creator.uid);
    batch.set(memberRef, {
      userId: creator.uid,
      role: 'manager' as TeamRole,
      addedAt: serverTimestamp(),
      addedBy: creator.uid,
      updatedAt: serverTimestamp()
    });

    // Audit log
    try {
      await logAuditEvent(
        AuditAction.TEAM_CREATED,
        {
          orgId,
          actorId: creator.uid,
          actorEmail: creator.email || undefined,
        },
        {
          targetId: teamRef.id,
          targetType: 'team',
          metadata: { teamName: team.name },
          description: `Created team "${team.name}"`
        }
      );
    } catch (error) {
      console.error('Failed to log audit event:', error);
    }
  }

  await batch.commit();

  // Log activity
  if (creator) {
    await logTeamActivity(orgId, {
      teamId: teamRef.id,
      type: 'team_created',
      actorId: creator.uid,
      actorName: creator.displayName || creator.email || undefined
    });
  }

  // Auto-create team digest config with defaults (enabled, weekly, Slack delivery)
  try {
    await createTeamReportConfig(orgId, teamRef.id, {});
  } catch (err) {
    console.warn('Failed to create default team report config:', err);
  }

  return teamRef.id;
}

export async function getOrgTeams(orgId: string) {
  const teamsRef = collection(db, 'orgs', orgId, 'teams');
  const snapshot = await getDocs(teamsRef);

  return snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id  // Ensure doc.id always takes precedence
    } as Team;
  });
}

// Get teams visible to a specific user (filters out private teams where user is not a member)
export async function getVisibleOrgTeams(orgId: string, userId: string) {
  const teamsRef = collection(db, 'orgs', orgId, 'teams');
  const snapshot = await getDocs(teamsRef);

  const allTeams = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id  // Ensure doc.id always takes precedence
    } as Team;
  });

  // Filter out deleted teams
  const activeTeams = allTeams.filter(team => !team.deletedAt);

  // Check if user is super admin - they can see all teams
  const { checkSuperAdmin } = await import('./permissions');
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const currentUser = auth.currentUser;

  if (currentUser) {
    const isSuperAdmin = await checkSuperAdmin(currentUser);
    if (isSuperAdmin) {
      // Super admins see all teams, no filtering needed
      return activeTeams;
    }
  }

  // Filter out private teams where user is not a member
  const visibleTeams = [];
  const seenIds = new Set<string>();

  for (const team of activeTeams) {
    // Skip teams with invalid IDs
    if (!team.id) {
      console.warn('[getVisibleOrgTeams] Skipping team with undefined ID:', team);
      continue;
    }

    // Skip duplicates
    if (seenIds.has(team.id)) {
      console.warn('[getVisibleOrgTeams] Duplicate team ID found:', team.id);
      continue;
    }
    seenIds.add(team.id);

    if (team.membershipType === 'private') {
      // Check if user is a member of this private team
      const membership = await getTeamMembership(orgId, team.id, userId);
      if (membership) {
        visibleTeams.push(team);
      }
      // If no membership, team is not visible to this user
    } else {
      // Team is open or closed, so it's visible
      visibleTeams.push(team);
    }
  }

  return visibleTeams;
}

// Get teams where user is a manager or owner (createdBy)
export async function getTeamsUserManages(orgId: string, userId: string) {
  const teamsRef = collection(db, 'orgs', orgId, 'teams');
  const snapshot = await getDocs(teamsRef);

  const allTeams = snapshot.docs.map(doc => {
    const data = doc.data();
    return {
      ...data,
      id: doc.id  // Ensure doc.id always takes precedence
    } as Team;
  });

  // Filter out deleted teams
  const activeTeams = allTeams.filter(team => !team.deletedAt);

  // Check if user is super admin by userId - they can manage all teams
  try {
    const userDocRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists() && userDoc.data()?.isSuperAdmin === true) {
      return activeTeams;
    }
  } catch (error) {
    console.error('[getTeamsUserManages] Error checking super admin status:', error);
    // Continue with normal flow if check fails
  }

  // Filter for teams where user is manager
  const managedTeams = [];

  for (const team of activeTeams) {
    // Skip teams with invalid IDs
    if (!team.id) {
      console.warn('[getTeamsUserManages] Skipping team with undefined ID:', team);
      continue;
    }

    // Check if user created the team (owner) - creators are always managers
    if (team.createdBy === userId) {
      managedTeams.push(team);
      continue;
    }

    // Check if user has manager role in memberships subcollection
    const membership = await getTeamMembership(orgId, team.id, userId);
    if (membership && membership.role === 'manager') {
      managedTeams.push(team);
    }
  }

  return managedTeams;
}

export async function getTeam(orgId: string, teamId: string) {
  const teamRef = doc(db, 'orgs', orgId, 'teams', teamId);
  const teamDoc = await getDoc(teamRef);
  
  if (!teamDoc.exists()) {
    return null;
  }
  
  return {
    id: teamDoc.id,
    ...teamDoc.data()
  } as Team;
}

export async function updateTeam(
  orgId: string,
  teamId: string,
  updates: Partial<Team>,
  actorId?: string,
  actorName?: string
) {
  const teamRef = doc(db, 'orgs', orgId, 'teams', teamId);

  // Get current team data for comparison
  const currentTeam = await getDoc(teamRef);
  const currentData = currentTeam.exists() ? currentTeam.data() : null;

  await updateDoc(teamRef, {
    ...updates,
    updatedAt: serverTimestamp()
  });

  // Log activity changes if we have actor info
  if (actorId && currentData) {
    // Log name change
    if (updates.name && updates.name !== currentData.name) {
      await logTeamActivity(orgId, {
        teamId,
        type: 'team_renamed',
        actorId,
        actorName,
        metadata: {
          fromValue: currentData.name,
          toValue: updates.name
        }
      });
    }

    // Log description change
    if (updates.description !== undefined && updates.description !== currentData.description) {
      await logTeamActivity(orgId, {
        teamId,
        type: 'description_changed',
        actorId,
        actorName
      });
    }

    // Log membership type change
    if (updates.membershipType && updates.membershipType !== currentData.membershipType) {
      await logTeamActivity(orgId, {
        teamId,
        type: 'membership_type_changed',
        actorId,
        actorName,
        metadata: {
          fromValue: currentData.membershipType,
          toValue: updates.membershipType
        }
      });
    }
  }
}

export async function deleteTeam(orgId: string, teamId: string, soft = true, performedBy?: string) {
  const teamRef = doc(db, 'orgs', orgId, 'teams', teamId);

  if (soft) {
    // Soft delete - mark as deleted and clean up active references
    const batch = writeBatch(db);

    // 1. Mark team as deleted
    batch.update(teamRef, {
      deletedAt: serverTimestamp(),
      archived: true,
      updatedAt: serverTimestamp(),
      deletedBy: performedBy || 'system'
    });

    // 2. Deactivate any standup configs for this team
    try {
      const standupConfigsRef = collection(db, 'orgs', orgId, 'standupConfigs');
      const standupConfigsQuery = query(standupConfigsRef, where('teamId', '==', teamId));
      const standupConfigsSnapshot = await getDocs(standupConfigsQuery);

      standupConfigsSnapshot.docs.forEach(doc => {
        batch.update(doc.ref, {
          isActive: false,
          deactivatedAt: serverTimestamp(),
          deactivatedReason: 'team_deleted'
        });
      });
    } catch (error) {
      console.error('Error deactivating standup configs:', error);
    }

    await batch.commit();

    // 3. Audit log
    if (performedBy) {
      try {
        await logAuditEvent(
          AuditAction.TEAM_DELETED,
          {
            orgId,
            actorId: performedBy,
          },
          {
            targetId: teamId,
            targetType: 'team',
            metadata: { soft: true },
            description: 'Team soft deleted'
          }
        );
      } catch (error) {
        console.error('Failed to log audit event:', error);
      }
    }

    // Note: We intentionally keep teamId/teamName references in updates
    // to maintain historical context. The UI should handle archived teams appropriately.

  } else {
    // Hard delete - permanently remove (use with extreme caution)
    const batch = writeBatch(db);

    // 1. Delete all team memberships first
    const membershipsRef = collection(db, 'orgs', orgId, 'teams', teamId, 'memberships');
    const membershipsSnapshot = await getDocs(membershipsRef);
    membershipsSnapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // 2. Remove team references from updates (but keep the updates)
    try {
      const updatesRef = collection(db, 'orgs', orgId, 'updates');
      const updatesQuery = query(updatesRef, where('teamId', '==', teamId));
      const updatesSnapshot = await getDocs(updatesQuery);

      updatesSnapshot.docs.forEach(doc => {
        batch.update(doc.ref, {
          teamId: null,
          teamName: null,
          updatedAt: serverTimestamp()
        });
      });
    } catch (error) {
      console.error('Error cleaning up updates:', error);
    }

    // 3. Delete standup configs for this team
    try {
      const standupConfigsRef = collection(db, 'orgs', orgId, 'standupConfigs');
      const standupConfigsQuery = query(standupConfigsRef, where('teamId', '==', teamId));
      const standupConfigsSnapshot = await getDocs(standupConfigsQuery);

      standupConfigsSnapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
    } catch (error) {
      console.error('Error deleting standup configs:', error);
    }

    // 4. Delete the team document itself
    batch.delete(teamRef);

    await batch.commit();

    // 5. Audit log
    if (performedBy) {
      try {
        await logAuditEvent(
          AuditAction.TEAM_DELETED,
          {
            orgId,
            actorId: performedBy,
          },
          {
            targetId: teamId,
            targetType: 'team',
            metadata: { soft: false },
            description: 'Team permanently deleted'
          }
        );
      } catch (error) {
        console.error('Failed to log audit event:', error);
      }
    }
  }
}

export async function addTeamMember(
  orgId: string,
  teamId: string,
  userId: string,
  role: TeamRole = 'member',
  addedBy: string,
  actorName?: string,
  memberName?: string
) {
  // Validate all required parameters to prevent invalid Firestore paths
  if (!orgId || !teamId || !userId) {
    throw new Error('addTeamMember called with invalid parameters: orgId, teamId, and userId are required');
  }

  const batch = writeBatch(db);

  // Add to membership subcollection
  const memberRef = doc(collection(db, 'orgs', orgId, 'teams', teamId, 'memberships'), userId);
  batch.set(memberRef, {
    userId,
    role,
    addedAt: serverTimestamp(),
    addedBy,
    updatedAt: serverTimestamp()
  });

  // Also update legacy memberIds array for backward compatibility
  const teamRef = doc(db, 'orgs', orgId, 'teams', teamId);
  batch.update(teamRef, {
    memberIds: arrayUnion(userId),
    updatedAt: serverTimestamp()
  });

  await batch.commit();

  // Audit log
  try {
    await logAuditEvent(
      AuditAction.TEAM_MEMBER_ADDED,
      {
        orgId,
        actorId: addedBy,
      },
      {
        targetId: userId,
        targetType: 'user',
        metadata: { teamId, role },
        description: `Added user to team with ${role} role`
      }
    );
  } catch (error) {
    console.error('Failed to log audit event:', error);
  }

  // Log activity
  await logTeamActivity(orgId, {
    teamId,
    type: 'member_added',
    actorId: addedBy,
    actorName,
    metadata: {
      memberId: userId,
      memberName,
      toRole: role
    }
  });
}

export async function removeTeamMember(
  orgId: string,
  teamId: string,
  userId: string,
  removedBy: string,
  actorName?: string,
  memberName?: string
) {
  // Validate all required parameters to prevent invalid Firestore paths
  if (!orgId || !teamId || !userId) {
    throw new Error('removeTeamMember called with invalid parameters: orgId, teamId, and userId are required');
  }

  // The membership subcollection is the source of truth. Its delete rule
  // permits self-removal, so a user leaving a team can always succeed here.
  // The legacy memberIds update is kept for backward compat but is not
  // allowed for viewer-role users — batching the two caused viewers' "leave
  // team" attempts to fail atomically (LB-119).
  const memberRef = doc(db, 'orgs', orgId, 'teams', teamId, 'memberships', userId);
  await deleteDoc(memberRef);

  // Best-effort update of the legacy memberIds array. Failure here doesn't
  // undo the authoritative membership delete above.
  try {
    const teamRef = doc(db, 'orgs', orgId, 'teams', teamId);
    await updateDoc(teamRef, {
      memberIds: arrayRemove(userId),
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.warn('Failed to update legacy memberIds on team doc; membership subcollection is authoritative:', error);
  }

  // Audit log
  try {
    await logAuditEvent(
      AuditAction.TEAM_MEMBER_REMOVED,
      {
        orgId,
        actorId: removedBy,
      },
      {
        targetId: userId,
        targetType: 'user',
        metadata: { teamId },
        description: `Removed user from team`
      }
    );
  } catch (error) {
    console.error('Failed to log audit event:', error);
  }

  // Log activity
  await logTeamActivity(orgId, {
    teamId,
    type: 'member_removed',
    actorId: removedBy,
    actorName,
    metadata: {
      memberId: userId,
      memberName
    }
  });
}

export async function getUserTeams(orgId: string, userId: string) {
  const teamsRef = collection(db, 'orgs', orgId, 'teams');
  const teamsQuery = query(
    teamsRef,
    where('memberIds', 'array-contains', userId)
  );

  const snapshot = await getDocs(teamsQuery);

  // Filter out soft-deleted teams
  return snapshot.docs
    .map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Team))
    .filter(team => !team.deletedAt);
}

// Get team membership for a user
export async function getTeamMembership(
  orgId: string,
  teamId: string,
  userId: string
): Promise<TeamMembership | null> {
  // Validate all required parameters to prevent invalid Firestore paths
  if (!orgId || !teamId || !userId) {
    console.error('getTeamMembership called with invalid parameters:', { orgId, teamId, userId });
    return null;
  }

  const memberRef = doc(db, 'orgs', orgId, 'teams', teamId, 'memberships', userId);
  const memberDoc = await getDoc(memberRef);

  if (!memberDoc.exists()) {
    return null;
  }

  return {
    id: memberDoc.id,
    ...memberDoc.data()
  } as TeamMembership;
}

// Get all team members with their roles
export async function getTeamMembers(
  orgId: string,
  teamId: string,
  excludeSuperAdmins: boolean = true
): Promise<TeamMembership[]> {
  const membersRef = collection(db, 'orgs', orgId, 'teams', teamId, 'memberships');
  const snapshot = await getDocs(membersRef);

  let members = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as TeamMembership[];

  // Filter out super admins if requested
  if (excludeSuperAdmins) {
    const userIds = members.map(m => m.userId || m.id).filter((uid): uid is string => uid !== undefined);
    const usersRef = collection(db, 'users');
    const userDocs = await Promise.all(
      userIds.map(uid => getDoc(doc(db, 'users', uid)))
    );

    const superAdminIds = new Set(
      userDocs
        .filter(doc => doc.exists() && doc.data()?.isSuperAdmin === true)
        .map(doc => doc.id)
    );

    members = members.filter(m => {
      const userId = m.userId || m.id;
      if (!userId) return true; // Keep members without userId
      return !superAdminIds.has(userId);
    });
  }

  return members;
}

// Update team member role
export async function updateTeamMemberRole(
  orgId: string,
  teamId: string,
  userId: string,
  newRole: TeamRole,
  updatedBy: string,
  actorName?: string,
  memberName?: string
) {
  // Validate all required parameters to prevent invalid Firestore paths
  if (!orgId || !teamId || !userId) {
    throw new Error('updateTeamMemberRole called with invalid parameters: orgId, teamId, and userId are required');
  }

  const memberRef = doc(db, 'orgs', orgId, 'teams', teamId, 'memberships', userId);

  // Get current role for audit
  const currentMember = await getDoc(memberRef);
  const oldRole = currentMember.exists() ? currentMember.data().role : null;

  await updateDoc(memberRef, {
    role: newRole,
    updatedAt: serverTimestamp()
  });

  // Audit log
  try {
    await logAuditEvent(
      AuditAction.TEAM_MEMBER_ROLE_CHANGED,
      {
        orgId,
        actorId: updatedBy,
      },
      {
        targetId: userId,
        targetType: 'user',
        metadata: { teamId, oldRole, newRole },
        description: `Changed team member role from ${oldRole} to ${newRole}`
      }
    );
  } catch (error) {
    console.error('Failed to log audit event:', error);
  }

  // Log activity
  await logTeamActivity(orgId, {
    teamId,
    type: 'member_role_changed',
    actorId: updatedBy,
    actorName,
    metadata: {
      memberId: userId,
      memberName,
      fromRole: oldRole,
      toRole: newRole
    }
  });
}

// Migrate team from memberIds array to membership subcollection
export async function migrateTeamMemberships(
  orgId: string,
  teamId: string
): Promise<void> {
  const team = await getTeam(orgId, teamId);
  if (!team) {
    throw new Error('Team not found');
  }

  const batch = writeBatch(db);
  const memberIds = team.memberIds || [];

  for (const userId of memberIds) {
    const memberRef = doc(collection(db, 'orgs', orgId, 'teams', teamId, 'memberships'), userId);
    const existingMember = await getDoc(memberRef);

    if (!existingMember.exists()) {
      // Determine role: creator gets manager, others get member
      const role: TeamRole = userId === team.createdBy ? 'manager' : 'member';

      batch.set(memberRef, {
        userId,
        role,
        addedAt: team.createdAt, // Use team creation date as approximation
        addedBy: team.createdBy, // Assume creator added them
        updatedAt: serverTimestamp()
      });
    }
  }

  await batch.commit();
}

// Check if user is a team manager (for authorization checks)
export async function isTeamManager(
  orgId: string,
  teamId: string,
  userId: string
): Promise<boolean> {
  const team = await getTeam(orgId, teamId);
  if (!team) return false;

  // Check if user created the team (backward compatibility)
  if (team.createdBy === userId) {
    return true;
  }

  // Check if user has manager role in team memberships subcollection
  const membership = await getTeamMembership(orgId, teamId, userId);
  return membership?.role === 'manager';
}